import React, {
    forwardRef,
    memo,
    useCallback,
    useEffect,
    useRef,
    useState,
    useImperativeHandle
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useContentfulContext } from '../../../contexts/ContentfulContext';
import AgGridLoadingOverlay from './AgGridLoadingOverlay';
import AgGridEmptyOverlay from './AgGridEmptyOverlay';

export { default as AgGridLoadingOverlay } from './AgGridLoadingOverlay';
export { default as AgGridEmptyOverlay } from './AgGridEmptyOverlay';

export const getAgGridSortStorageKey = (tableId) => `agGridSortModel_${tableId}`;
export const getAgGridFilterStorageKey = (tableId) => `agGridFilterModel_${tableId}`;
export const getAgGridColumnVisibilityStorageKey = (tableId) => `agGridColumnVisibility_${tableId}`;

/**
 * Format an AG Grid filter model value into a clean, human-readable string.
 */
const formatFilterValue = (model) => {
    if (!model) return '';
    // Multi-condition (AND / OR)
    if (model.condition1 || model.condition2) {
        const c1 = formatFilterValue(model.condition1);
        const c2 = formatFilterValue(model.condition2);
        const op = model.operator ? ` ${model.operator.toLowerCase()} ` : ' & ';
        return [c1, c2].filter(Boolean).join(op);
    }
    // Set filter (array of selected values)
    if (Array.isArray(model.values)) {
        if (model.values.length === 0) return 'None';
        if (model.values.length <= 2) return model.values.join(', ');
        return `${model.values.slice(0, 2).join(', ')} (+${model.values.length - 2})`;
    }
    // Date filter
    if (model.dateFrom) {
        const from = String(model.dateFrom).split(' ')[0];
        if (model.dateTo) {
            const to = String(model.dateTo).split(' ')[0];
            return `${from} – ${to}`;
        }
        if (model.type === 'lessThan') return `< ${from}`;
        if (model.type === 'greaterThan') return `> ${from}`;
        return from;
    }
    // Text / Number filter
    if (model.filter !== undefined && model.filter !== null) {
        const val = String(model.filter);
        if (model.type === 'notEqual') return `≠ "${val}"`;
        if (model.type === 'notContains') return `excludes "${val}"`;
        if (model.type === 'startsWith') return `starts with "${val}"`;
        if (model.type === 'endsWith') return `ends with "${val}"`;
        if (model.type === 'greaterThan') return `> ${val}`;
        if (model.type === 'lessThan') return `< ${val}`;
        return val;
    }
    return 'Applied';
};

const AgGridWrapper = memo(forwardRef((props, ref) => {
    const { enums, content } = useContentfulContext();
    const {
        tableId = 'default-table',
        theme = 'ag-theme-quartz',
        showFilterBar = true,
        maxVisibleFilters = 3,
        loading = false,
        emptyTitle,
        emptyMessage,
        loadingOverlayComponent = AgGridLoadingOverlay,
        noRowsOverlayComponent = AgGridEmptyOverlay,
        loadingOverlayComponentParams,
        noRowsOverlayComponentParams,
        onSortChanged: parentOnSortChanged,
        onGridReady: parentOnGridReady,
        onFilterChanged: parentOnFilterChanged,
        ...restProps
    } = props;

    const [gridApi, setGridApi] = useState(null);
    const [activeFilters, setActiveFilters] = useState([]);
    const [hasActiveSorting, setHasActiveSorting] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const getSortStorageKey = useCallback(() => getAgGridSortStorageKey(tableId), [tableId]);
    const getFilterStorageKey = useCallback(() => getAgGridFilterStorageKey(tableId), [tableId]);

    const isInitialSortApplied = useRef(false);
    const isInitialFilterApplied = useRef(false);

    const defaultProps = {
        domLayout: 'autoHeight',
        pagination: true,
        paginationPageSize: 10,
        paginationPageSizeSelector: enums?.PAGINATION_PAGE_SIZE_SELECTOR,
    };

    /** Sync active filter state & sorting indicators */
    const syncActiveFiltersAndSorting = useCallback(() => {
        if (!gridApi?.api) return;

        // Parse sorting
        const sortingActive = (gridApi.columnApi?.getColumnState() || []).some(col => col.sort != null);
        setHasActiveSorting(sortingActive);

        // Parse filters
        const filterModel = gridApi.api.getFilterModel() || {};
        const filterKeys = Object.keys(filterModel);

        if (filterKeys.length === 0) {
            setActiveFilters([]);
            return;
        }

        const parsed = filterKeys.map((colId) => {
            const model = filterModel[colId];
            const colDef = gridApi.api.getColumnDef(colId);
            const headerName = colDef?.headerName || colId;
            const displayValue = formatFilterValue(model);
            return { colId, headerName, displayValue };
        });

        setActiveFilters(parsed);
    }, [gridApi]);

    /** Save sort model to localStorage */
    const handleSortChanged = useCallback(() => {
        if (!gridApi || !gridApi.columnApi) return;
        const sortModel = gridApi.columnApi.getColumnState().filter(col => col.sort != null);
        try {
            if (sortModel.length > 0) {
                localStorage.setItem(getSortStorageKey(), JSON.stringify(sortModel));
            } else {
                localStorage.removeItem(getSortStorageKey());
            }
        } catch (err) {
            console.error('Failed to save sort model:', err);
        }
        syncActiveFiltersAndSorting();
        if (typeof parentOnSortChanged === 'function') {
            parentOnSortChanged();
        }
    }, [gridApi, getSortStorageKey, parentOnSortChanged, syncActiveFiltersAndSorting]);

    /** Save filter model to localStorage and update active chips */
    const handleFilterChanged = useCallback(() => {
        if (!gridApi?.api) return;
        const filterModel = gridApi.api.getFilterModel() || {};
        try {
            if (Object.keys(filterModel).length > 0) {
                localStorage.setItem(getFilterStorageKey(), JSON.stringify(filterModel));
            } else {
                localStorage.removeItem(getFilterStorageKey());
            }
        } catch (err) {
            console.error('Failed to save filter model:', err);
        }
        syncActiveFiltersAndSorting();
        if (typeof parentOnFilterChanged === 'function') {
            parentOnFilterChanged();
        }
    }, [gridApi, getFilterStorageKey, parentOnFilterChanged, syncActiveFiltersAndSorting]);

    /** Restore sort/filter on grid ready */
    const handleGridReady = useCallback((params) => {
        setGridApi(params);
        if (loading && params?.api) {
            params.api.showLoadingOverlay();
        }
        if (typeof parentOnGridReady === 'function') {
            parentOnGridReady(params);
        }
    }, [parentOnGridReady, loading]);

    /** Synchronize loading & empty state overlays with gridApi */
    useEffect(() => {
        if (!gridApi?.api) return;
        if (loading) {
            gridApi.api.showLoadingOverlay();
        } else {
            if (Array.isArray(restProps.rowData) && restProps.rowData.length === 0) {
                gridApi.api.showNoRowsOverlay();
            } else {
                gridApi.api.hideOverlay();
            }
        }
    }, [loading, gridApi, restProps.rowData]);

    useEffect(() => {
        if (!gridApi || isInitialSortApplied.current) return;
        try {
            const saved = localStorage.getItem(getSortStorageKey());
            if (saved) {
                const sortModel = JSON.parse(saved);
                if (sortModel?.length > 0 && gridApi.columnApi) {
                    const currentSortModel = gridApi.columnApi.getColumnState();
                    const mergeSortModel = currentSortModel.map(o1 => {
                        const res = sortModel.find(o2 => o2.colId === o1.colId);
                        if (res) return res;
                        else return { ...o1, sort: null };
                    });
                    gridApi.columnApi.applyColumnState({ state: mergeSortModel });
                    isInitialSortApplied.current = true;
                    syncActiveFiltersAndSorting();
                }
            }
        } catch (err) {
            console.error('Failed to restore sort model from localStorage:', err);
        }
    }, [gridApi, getSortStorageKey, syncActiveFiltersAndSorting]);

    useEffect(() => {
        if (!gridApi || isInitialFilterApplied.current) return;
        try {
            const saved = localStorage.getItem(getFilterStorageKey());
            if (saved) {
                const filterModel = JSON.parse(saved);
                if (filterModel && Object.keys(filterModel).length > 0) {
                    gridApi.api.setFilterModel(filterModel);
                    isInitialFilterApplied.current = true;
                    syncActiveFiltersAndSorting();
                }
            }
        } catch (err) {
            console.error('Failed to restore filter model from localStorage:', err);
        }
    }, [gridApi, getFilterStorageKey, syncActiveFiltersAndSorting]);

    /** --- CLEAR & REMOVE METHODS --- */
    const removeSingleFilter = useCallback((colId) => {
        if (!gridApi?.api) return;
        const currentModel = { ...(gridApi.api.getFilterModel() || {}) };
        delete currentModel[colId];
        gridApi.api.setFilterModel(currentModel);

        try {
            if (Object.keys(currentModel).length > 0) {
                localStorage.setItem(getFilterStorageKey(), JSON.stringify(currentModel));
            } else {
                localStorage.removeItem(getFilterStorageKey());
            }
        } catch (err) {
            console.error('Failed to update filter model in localStorage:', err);
        }

        syncActiveFiltersAndSorting();
        if (typeof parentOnFilterChanged === 'function') {
            parentOnFilterChanged();
        }
    }, [gridApi, getFilterStorageKey, syncActiveFiltersAndSorting, parentOnFilterChanged]);

    const clearAllSorting = useCallback(() => {
        if (!gridApi?.columnApi) return;
        gridApi.columnApi.applyColumnState({ defaultState: { sort: null } });
        localStorage.removeItem(getSortStorageKey());
        setHasActiveSorting(false);
    }, [gridApi, getSortStorageKey]);

    const clearAllFilters = useCallback(() => {
        if (!gridApi?.api) return;
        gridApi.api.setFilterModel(null);
        localStorage.removeItem(getFilterStorageKey());
        setActiveFilters([]);
        setIsExpanded(false);
        if (typeof parentOnFilterChanged === 'function') {
            parentOnFilterChanged();
        }
    }, [gridApi, getFilterStorageKey, parentOnFilterChanged]);

    const clearAll = useCallback(() => {
        clearAllSorting();
        clearAllFilters();
    }, [clearAllSorting, clearAllFilters]);

    /** Expose API to parent */
    useImperativeHandle(ref, () => ({
        clearAllSorting,
        clearAllFilters,
        clearAll,
        removeSingleFilter
    }));

    const mergedProps = {
        ...defaultProps,
        loadingOverlayComponent,
        loadingOverlayComponentParams,
        noRowsOverlayComponent,
        noRowsOverlayComponentParams: {
            noRowsTitle: emptyTitle,
            noRowsMessage: emptyMessage,
            ...noRowsOverlayComponentParams,
        },
        ...restProps,
        onSortChanged: handleSortChanged,
        onFilterChanged: handleFilterChanged,
        onGridReady: handleGridReady,
    };

    const hasAnyFilterOrSort = activeFilters.length > 0 || hasActiveSorting;
    const hasOverflow = activeFilters.length > maxVisibleFilters;
    const visibleFilters = isExpanded ? activeFilters : activeFilters.slice(0, maxVisibleFilters);
    const hiddenCount = activeFilters.length - maxVisibleFilters;

    return (
        <div className="w-full max-w-full overflow-x-auto transition-all duration-300">
            {/* Active Filters & Sorting Toolbar */}
            {showFilterBar && hasAnyFilterOrSort && (
                <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2 bg-surface-alt px-3 py-2 rounded-lg border border-border-table text-xs shadow-xs">
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                        {activeFilters.length > 0 && (
                            <>
                                <span className="font-semibold text-slate-700 flex items-center gap-1.5 mr-1 shrink-0">
                                    <Filter className="w-3.5 h-3.5 text-primary" />
                                    <span>Filters Applied ({activeFilters.length}):</span>
                                </span>
                                {visibleFilters.map(({ colId, headerName, displayValue }) => (
                                    <span
                                        key={colId}
                                        className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-0.5 rounded-full bg-white border border-border-table text-slate-700 shadow-xs hover:border-slate-300 transition-colors"
                                        title={`Filter on ${headerName}: ${displayValue}`}
                                    >
                                        <span className="text-slate-500 font-medium">{headerName}:</span>
                                        <span className="font-semibold text-slate-900 max-w-[150px] truncate">{displayValue}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeSingleFilter(colId)}
                                            className="p-0.5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                                            title={`Remove ${headerName} filter`}
                                            aria-label={`Remove ${headerName} filter`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ))}

                                {hasOverflow && !isExpanded && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded(true)}
                                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-200/90 hover:bg-slate-300 text-slate-700 font-semibold text-xs transition-colors cursor-pointer"
                                        title={`Show ${hiddenCount} more filter${hiddenCount > 1 ? 's' : ''}`}
                                    >
                                        <span>+{hiddenCount} more</span>
                                        <ChevronDown className="w-3 h-3 text-slate-500" />
                                    </button>
                                )}

                                {hasOverflow && isExpanded && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded(false)}
                                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-200/90 hover:bg-slate-300 text-slate-700 font-semibold text-xs transition-colors cursor-pointer"
                                        title="Show fewer filters"
                                    >
                                        <span>Show less</span>
                                        <ChevronUp className="w-3 h-3 text-slate-500" />
                                    </button>
                                )}
                            </>
                        )}
                        {activeFilters.length === 0 && hasActiveSorting && (
                            <span className="text-slate-500 font-medium italic">
                                Custom sorting applied
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2.5 shrink-0 ml-auto">
                        {activeFilters.length > 0 && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="text-xs font-medium text-slate-500 hover:text-rose-600 underline cursor-pointer transition-colors"
                                title="Clear all column filters"
                            >
                                {content?.AG_GRID_WRAPPER_CONTENT?.clear_all_filter_cta || "Clear all filters"}
                            </button>
                        )}
                        {hasActiveSorting && (
                            <button
                                type="button"
                                onClick={clearAllSorting}
                                className="text-xs font-medium text-slate-500 hover:text-slate-800 underline cursor-pointer transition-colors"
                                title="Reset column sorting"
                            >
                                Reset sorting
                            </button>
                        )}
                    </div>
                </div>
            )}

            <div className={`${theme} w-full min-h-[300px] relative`}>
                <AgGridReact
                    ref={ref}
                    {...mergedProps}
                />
            </div>
        </div>
    );
}));

export default AgGridWrapper;
